import {
  Button,
  Card,
  CardContent,
  DataTable,
  type SortingState,
} from "@repo/ui";
import {
  calculerScores,
  motPluriel,
  pluriel,
  type EntStructQuestionEtat,
} from "@repo/utils";
import { Download, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { colonnesResultats } from "./colonnesResultats";
import { InbasketCampagneTable } from "./InbasketCampagneTable";
import { PamiCampagneTable } from "./PamiCampagneTable";
import {
  Copsoq3CampagneTable,
  type Copsoq3EvalRow,
} from "./Copsoq3CampagneTable";
import {
  FitCulturelCampagneTable,
  type FitCulturelEvalRow,
} from "./FitCulturelCampagneTable";

export type EvaluationData = {
  _id: string;
  parcoursId: string;
  nomCandidat?: string;
  prenomCandidat?: string;
  dateFin?: number;
  questionsEtat?: EntStructQuestionEtat[];
  isSuspect: boolean;
};

export type EvaluationWithScores = EvaluationData & {
  scores: ReturnType<typeof calculerScores> | null;
  globalPercentage: number;
};

/**
 * Le test affiché n'est PLUS choisi ici : il vient du sélecteur global du détail
 * de campagne, qui pilote aussi les onglets référentiel. Deux sélecteurs pour la
 * même notion se contrediraient à l'écran.
 */
export type TestCampagne =
  | "inbasket"
  | "pami"
  | "entstruct"
  | "fitculturel"
  | "copsoq3";

/** Ordre par défaut de la table : les passations les plus récentes d'abord. */
const TRI_INITIAL: SortingState = [{ id: "date", desc: true }];

interface CampagneResultatsTabProps {
  /** Test sélectionné dans l'en-tête de la campagne. */
  testActif: TestCampagne | undefined;
  evaluationsInbasket: { _id: string; parcoursId: string; nomCandidat?: string; prenomCandidat?: string; clientResultsJson?: string; clientResultsGradesJson?: string }[] | undefined;
  evaluationsPami: { _id: string; parcoursId: string; nomCandidat?: string; prenomCandidat?: string; scoresJson?: string }[] | undefined;
  evaluationsFitCulturel: FitCulturelEvalRow[] | undefined;
  /** Items du référentiel fit-culturel, pour libeller les colonnes. */
  itemsFitCulturel?: { code: string; label: string }[];
  evaluationsCopsoq3: Copsoq3EvalRow[] | undefined;
  /** Nom de la campagne, pour nommer les fichiers exportés. */
  campagneNom?: string;
  evaluations: EvaluationData[] | undefined;
  evaluationsWithScores: EvaluationWithScores[];
  allCompetences: { nom: string }[];
  onExportCSV: () => void;
  getEvaluationHref: (evaluation: EvaluationWithScores) => string;
  actionLabel?: string;
}

export function CampagneResultatsTab({
  testActif,
  evaluationsInbasket,
  evaluationsPami,
  evaluationsFitCulturel,
  itemsFitCulturel,
  evaluationsCopsoq3,
  campagneNom,
  evaluations,
  evaluationsWithScores,
  allCompetences,
  onExportCSV,
  getEvaluationHref,
  actionLabel,
}: CampagneResultatsTabProps) {
  const showInbasket = testActif === "inbasket";
  const showPami = testActif === "pami";
  const showFitCulturel = testActif === "fitculturel";
  const showEntStruct = testActif === "entstruct";
  const showCopsoq3 = testActif === "copsoq3";

  // Colonnes dynamiques : une par compétence du barème. Mémoïsées sur la liste
  // des compétences — une nouvelle référence remonterait la table entière.
  const colonnes = useMemo(
    () =>
      colonnesResultats({
        competences: allCompetences,
        libelleAction: actionLabel,
      }),
    [allCompetences, actionLabel],
  );

  const [tri, setTri] = useState<SortingState>(TRI_INITIAL);

  return (
    <>
      {showInbasket && (
        <div className="mb-6">
          {evaluationsInbasket === undefined ? (
            <Card>
              <CardContent className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2">Chargement des évaluations...</span>
              </CardContent>
            </Card>
          ) : (
            <InbasketCampagneTable evaluations={evaluationsInbasket} />
          )}
        </div>
      )}

      {showPami && (
        <div className="mb-6">
          {evaluationsPami === undefined ? (
            <Card>
              <CardContent className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2">Chargement des évaluations...</span>
              </CardContent>
            </Card>
          ) : (
            <PamiCampagneTable evaluations={evaluationsPami} />
          )}
        </div>
      )}

      {showFitCulturel && (
        <div className="mb-6">
          {evaluationsFitCulturel === undefined ? (
            <Card>
              <CardContent className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2">Chargement des évaluations...</span>
              </CardContent>
            </Card>
          ) : (
            <FitCulturelCampagneTable
              evaluations={evaluationsFitCulturel}
              dimensions={itemsFitCulturel}
            />
          )}
        </div>
      )}

      {showCopsoq3 && (
        <div className="mb-6">
          {evaluationsCopsoq3 === undefined ? (
            <Card>
              <CardContent className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2">Chargement des évaluations...</span>
              </CardContent>
            </Card>
          ) : (
            <Copsoq3CampagneTable
              evaluations={evaluationsCopsoq3}
              campagneNom={campagneNom}
            />
          )}
        </div>
      )}

      {showEntStruct && (
        <div className="mb-6">
          <div className="mb-4 flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {evaluations === undefined
                ? "Chargement des évaluations..."
                : `${pluriel(evaluations.length, "candidat")} ${motPluriel(evaluations.length, "évalué")} en entretien FairView`}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={onExportCSV}
              disabled={evaluationsWithScores.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter CSV
            </Button>
          </div>
          <DataTable
            colonnes={colonnes}
            donnees={evaluations === undefined ? undefined : evaluationsWithScores}
            cleColonnes="campagne-resultats"
            tri={tri}
            onTriChange={setTri}
            hrefLigne={getEvaluationHref}
            vide={{
              titre: "Aucune évaluation FairView",
              description:
                "Aucun candidat n'a terminé d'entretien FairView dans cette campagne.",
            }}
          />
        </div>
      )}
    </>
  );
}
